import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const SERVICE_LABELS = {
  laser_scanner: "Rilievo Laser Scanner 3D", accesso_atti: "Accesso agli Atti", report_asseverato: "Report Asseverato", audit_urbanistico: "Audit Tecnico-Urbanistico",
  delega_catasto: "Delega estrazione catastale", delega_accesso_atti: "Delega accesso agli atti",
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", data: null });

  useEffect(() => {
    if (!sessionId) { setState({ status: "error" }); return; }
    let attempts = 0, active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") { setState({ status: "paid", data }); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState({ status: "failed", data }); return; }
      } catch (e) { /* retry */ }
      if (++attempts < 6 && active) setTimeout(poll, 2000);
      else if (active) setState((s) => (s.status === "polling" ? { status: "timeout", data: s.data } : s));
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  const praticaId = state.data?.pratica_id;
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B0F17] text-slate-100 px-5">
      <div data-testid="payment-success-card" className="max-w-md w-full rounded-2xl border border-slate-800 bg-[#121824] p-8 text-center">
        {state.status === "polling" && (<>
          <Loader2 className="h-10 w-10 text-cyan-400 animate-spin mx-auto mb-4" />
          <h1 className="font-heading text-xl font-bold">Verifica del pagamento…</h1>
          <p data-testid="payment-status-text" className="text-sm text-slate-400 mt-2">Attendi qualche secondo, stiamo confermando la transazione con Stripe.</p>
        </>)}
        {state.status === "paid" && (<>
          <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
          <h1 className="font-heading text-xl font-bold">Pagamento completato</h1>
          <p data-testid="payment-status-text" className="text-sm text-slate-400 mt-2">
            Servizio attivato: <span className="text-slate-200">{(state.data?.service_keys || [state.data?.service_key]).filter(Boolean).map((k) => SERVICE_LABELS[k] || k.replace(/_/g, " ")).join(" + ")}</span> · {Number(state.data?.amount || 0).toLocaleString("it-IT", { style: "currency", currency: (state.data?.currency || "eur").toUpperCase() })}
          </p>
        </>)}
        {(state.status === "failed" || state.status === "error") && (<>
          <XCircle className="h-12 w-12 text-rose-400 mx-auto mb-4" />
          <h1 className="font-heading text-xl font-bold">Pagamento non riuscito</h1>
          <p data-testid="payment-status-text" className="text-sm text-slate-400 mt-2">La transazione non è stata completata. Nessun addebito effettuato.</p>
        </>)}
        {state.status === "timeout" && (<>
          <Loader2 className="h-10 w-10 text-amber-400 mx-auto mb-4" />
          <h1 className="font-heading text-xl font-bold">Conferma in corso</h1>
          <p data-testid="payment-status-text" className="text-sm text-slate-400 mt-2">Il pagamento è in elaborazione: lo stato verrà aggiornato nella pratica appena confermato.</p>
        </>)}
        <button data-testid="payment-back-btn" onClick={() => navigate(praticaId ? `/pratica/${praticaId}` : "/dashboard")}
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-cyan-500 text-slate-900 font-semibold hover:bg-cyan-400">
          {praticaId ? "Torna alla pratica" : "Vai alle pratiche"} <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
